import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import proxy from "../../proxy";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Badge,
  Spinner,
  Center,
  SimpleGrid,
  Input,
  Select,
  IconButton,
  Code,
  useToast,
  Collapse,
  Tooltip,
} from "@chakra-ui/react";
import {
  ArrowBackIcon,
  RepeatIcon,
  DeleteIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";

// ─── Config nivele ───────────────────────────────────────────
const LEVELS = {
  fatal:      { label: "Fatal",      color: "red" },
  error:      { label: "Error",      color: "orange" },
  warning:    { label: "Warning",    color: "yellow" },
  notice:     { label: "Notice",     color: "blue" },
  deprecated: { label: "Deprecated", color: "purple" },
};

const levelMeta = (lvl) => LEVELS[lvl] || { label: lvl || "Log", color: "gray" };

const formatBytes = (b) => {
  if (!b) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return `${(b / Math.pow(k, i)).toFixed(i ? 1 : 0)} ${sizes[i]}`;
};

// ─── O singura intrare de eroare (expandabila) ───────────────
const ErrorRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const meta = levelMeta(entry.level);
  const lines = entry.message.split("\n");
  const firstLine = lines[0];
  const hasMore = lines.length > 1;

  return (
    <Box
      bg="rgba(26,32,44,0.6)"
      borderRadius="lg"
      border="1px solid"
      borderColor="whiteAlpha.200"
      borderLeft="3px solid"
      borderLeftColor={`${meta.color}.400`}
      p={3}
      _hover={{ borderColor: `${meta.color}.400` }}
      transition="0.2s"
    >
      <HStack align="start" spacing={3}>
        {hasMore ? (
          <IconButton
            aria-label="Toggle details"
            icon={open ? <ChevronDownIcon /> : <ChevronRightIcon />}
            size="xs"
            variant="ghost"
            color="gray.400"
            onClick={() => setOpen((v) => !v)}
          />
        ) : (
          <Box w="24px" />
        )}

        <VStack align="stretch" spacing={1} flex="1" minW={0}>
          <HStack spacing={2} flexWrap="wrap">
            <Badge colorScheme={meta.color} fontSize="2xs" px={2} borderRadius="full">
              {meta.label}
            </Badge>
            <Text fontSize="2xs" color="gray.500">
              {entry.timestamp || "—"}
            </Text>
            <Badge variant="subtle" colorScheme="gray" fontSize="2xs">
              📄 {entry.file}
            </Badge>
          </HStack>

          <Text
            fontFamily="mono"
            fontSize="xs"
            color="gray.200"
            whiteSpace="pre-wrap"
            wordBreak="break-word"
          >
            {firstLine}
          </Text>

          {hasMore && (
            <Collapse in={open} animateOpacity>
              <Code
                display="block"
                whiteSpace="pre-wrap"
                wordBreak="break-word"
                fontSize="2xs"
                bg="blackAlpha.500"
                color="gray.300"
                p={2}
                mt={1}
                borderRadius="md"
              >
                {lines.slice(1).join("\n")}
              </Code>
            </Collapse>
          )}
        </VStack>
      </HStack>
    </Box>
  );
};

const AdminErrors = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const [data, setData] = useState({ files: [], entries: [], totalEntries: 0, returned: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // filtre
  const [levelFilter, setLevelFilter] = useState("all");
  const [fileFilter, setFileFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchLogs = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${proxy}server_mts/admin/GetErrorLogs.php`, {
        headers: { "Cache-Control": "no-cache" },
        withCredentials: true,
      });
      setData(res.data || { files: [], entries: [] });
    } catch (e) {
      setError("Could not load error logs.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearLog = async ({ file, all }) => {
    const label = all ? "ALL log files" : file;
    if (!window.confirm(`Clear ${label}? This empties the log file(s) permanently.`)) return;
    try {
      await axios.post(
        `${proxy}server_mts/admin/ClearErrorLog.php`,
        all ? { all: true } : { file },
        { withCredentials: true }
      );
      toast({ title: "Cleared", status: "success", duration: 2000, position: "top" });
      fetchLogs();
    } catch {
      toast({ title: "Failed to clear log", status: "error", duration: 3000, position: "top" });
    }
  };

  // ─── Statistici + filtrare ─────────────────────────────────
  const counts = useMemo(() => {
    const c = { fatal: 0, error: 0, warning: 0, notice: 0, deprecated: 0 };
    data.entries.forEach((e) => {
      if (c[e.level] !== undefined) c[e.level] += 1;
    });
    return c;
  }, [data.entries]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data.entries.filter((e) => {
      if (levelFilter !== "all" && e.level !== levelFilter) return false;
      if (fileFilter !== "all" && e.file !== fileFilter) return false;
      if (q && !e.message.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data.entries, levelFilter, fileFilter, search]);

  return (
    <Box minH="100vh" bg="gray.900">
      {/* Header */}
      <Box
        bg="linear-gradient(135deg, rgba(26,32,44,0.98), rgba(45,55,72,0.95))"
        borderBottom="1px solid"
        borderColor="whiteAlpha.200"
        py={4}
        px={6}
        boxShadow="0 4px 20px rgba(0,0,0,0.3)"
      >
        <Container maxW="1400px">
          <HStack justify="space-between">
            <HStack spacing={3}>
              <IconButton
                aria-label="Back"
                icon={<ArrowBackIcon />}
                variant="ghost"
                colorScheme="teal"
                onClick={() => navigate("/admin/dashboard")}
              />
              <VStack align="start" spacing={0}>
                <Heading size="md" bgGradient="linear(to-r, red.300, orange.400)" bgClip="text">
                  🐛 Error Logs
                </Heading>
                <Text fontSize="xs" color="gray.500">
                  Server errors from server_mts
                </Text>
              </VStack>
            </HStack>

            <HStack spacing={2}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                leftIcon={<RepeatIcon />}
                onClick={fetchLogs}
                isLoading={isLoading}
              >
                Refresh
              </Button>
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                leftIcon={<DeleteIcon />}
                onClick={() => clearLog({ all: true })}
                isDisabled={data.files.length === 0}
              >
                Clear all
              </Button>
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Container maxW="1400px" py={8}>
        {isLoading ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Spinner size="xl" color="teal.400" thickness="4px" />
              <Text color="gray.400">Loading error logs...</Text>
            </VStack>
          </Center>
        ) : error ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Text color="red.300">{error}</Text>
              <Button size="sm" colorScheme="teal" onClick={fetchLogs}>
                Retry
              </Button>
            </VStack>
          </Center>
        ) : (
          <VStack spacing={6} align="stretch">
            {/* Stat tiles */}
            <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={4}>
              <StatTile label="Total" value={data.totalEntries} color="teal" />
              {Object.entries(LEVELS).map(([key, meta]) => (
                <StatTile
                  key={key}
                  label={meta.label}
                  value={counts[key] || 0}
                  color={meta.color}
                />
              ))}
            </SimpleGrid>

            {/* Files summary */}
            {data.files.length > 0 && (
              <Box
                bg="rgba(26,32,44,0.6)"
                borderRadius="xl"
                border="1px solid"
                borderColor="whiteAlpha.200"
                p={4}
              >
                <Text fontSize="sm" color="gray.300" fontWeight="bold" mb={3}>
                  📁 Log files ({data.files.length})
                </Text>
                <VStack align="stretch" spacing={2}>
                  {data.files.map((f) => (
                    <HStack
                      key={f.file}
                      justify="space-between"
                      bg="whiteAlpha.50"
                      p={2}
                      borderRadius="md"
                    >
                      <HStack spacing={2} minW={0}>
                        <Text fontSize="xs" color="gray.300" fontFamily="mono" isTruncated>
                          {f.file}
                        </Text>
                        <Badge colorScheme="gray" fontSize="2xs">
                          {formatBytes(f.size)}
                        </Badge>
                      </HStack>
                      <Tooltip label="Clear this file">
                        <IconButton
                          aria-label="Clear file"
                          icon={<DeleteIcon />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => clearLog({ file: f.file })}
                        />
                      </Tooltip>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            )}

            {/* Filters */}
            <HStack spacing={3} flexWrap="wrap">
              <Select
                size="sm"
                maxW="180px"
                bg="gray.800"
                borderColor="whiteAlpha.300"
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value)}
              >
                <option value="all">All levels</option>
                {Object.entries(LEVELS).map(([key, meta]) => (
                  <option key={key} value={key}>
                    {meta.label}
                  </option>
                ))}
              </Select>

              <Select
                size="sm"
                maxW="260px"
                bg="gray.800"
                borderColor="whiteAlpha.300"
                value={fileFilter}
                onChange={(e) => setFileFilter(e.target.value)}
              >
                <option value="all">All files</option>
                {data.files.map((f) => (
                  <option key={f.file} value={f.file}>
                    {f.file}
                  </option>
                ))}
              </Select>

              <Input
                size="sm"
                maxW="300px"
                bg="gray.800"
                borderColor="whiteAlpha.300"
                placeholder="Search message..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <Text fontSize="xs" color="gray.500">
                {filtered.length} shown
                {data.totalEntries > data.returned &&
                  ` · showing latest ${data.returned} of ${data.totalEntries}`}
              </Text>
            </HStack>

            {/* Entries */}
            {filtered.length === 0 ? (
              <Center
                py={16}
                bg="rgba(26,32,44,0.6)"
                borderRadius="xl"
                border="1px dashed"
                borderColor="whiteAlpha.200"
              >
                <VStack spacing={2}>
                  <Text fontSize="3xl">✅</Text>
                  <Text color="gray.400">
                    {data.entries.length === 0
                      ? "No errors logged. Everything's clean!"
                      : "No entries match the filters."}
                  </Text>
                </VStack>
              </Center>
            ) : (
              <VStack align="stretch" spacing={2}>
                {filtered.map((entry, i) => (
                  <ErrorRow key={i} entry={entry} />
                ))}
              </VStack>
            )}
          </VStack>
        )}
      </Container>
    </Box>
  );
};

const StatTile = ({ label, value, color }) => (
  <Box
    p={4}
    bg="rgba(26,32,44,0.6)"
    borderRadius="lg"
    border="1px solid"
    borderColor="whiteAlpha.200"
    textAlign="center"
  >
    <Heading size="lg" color={`${color}.300`}>
      {value}
    </Heading>
    <Text fontSize="xs" color="gray.500" fontWeight="semibold">
      {label}
    </Text>
  </Box>
);

export default AdminErrors;
